export const PDFTRON_SUPPORTED_EXTENSIONS = [
    'pdf',
    'doc',
    'docx',
    'ppt',
    'pptx',
    'xlsx',
    'xls',
    'csv',
    'png',
    'jpg',
    'jpeg',
    'gif',
    'bmp',
    'tiff',
    'tif',
    'txt',
    'rtf',
    'md',
    'markdown',
];

/** Preview in-app without WebViewer (fetch + render). */
export const NATIVE_TEXT_PREVIEW_EXTENSIONS = ['html', 'htm', 'txt'];

/** Office formats viewed as PDF via WebViewer Server (view-only, no Office Editor license needed). */
export const OFFICE_SERVER_PDF_EXTENSIONS = ['doc', 'docx', 'ppt', 'pptx'];

/** Spreadsheets also convert to PDF via WebViewer Server (Spreadsheet Editor is not licensed). */
export const SPREADSHEET_SERVER_PDF_EXTENSIONS = ['xlsx', 'xls', 'csv'];

/**
 * Keep in sync with WebViewer Server TRN_MAX_EXCEL_CELL_COUNT.
 * This value is sent as ExcelMaxAllowedCellCount on loadDocument and is what the
 * converter reports in the error. ~100k unique cells ≈ 400MB RAM.
 */
export const EXCEL_MAX_ALLOWED_CELL_COUNT = 1_000_000;

export const EXCEL_TOO_LARGE_PREVIEW_MESSAGE =
    'This spreadsheet is too large to preview. Download the original file and open it in Excel.';

export const isSpreadsheetPreviewExtension = extension => {
    return SPREADSHEET_SERVER_PDF_EXTENSIONS.includes(String(extension || '').toLowerCase());
};

export const getSpreadsheetOfficeOptions = () => ({
    formatOptions: {
        ExcelMaxAllowedCellCount: EXCEL_MAX_ALLOWED_CELL_COUNT,
    },
});

export const getFileExtensionFromUrl = filenameOrUrl => {
    if (!filenameOrUrl) return null;
    const path = String(filenameOrUrl).split('?')[0];
    const parts = path.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : null;
};

const MIME_TO_EXTENSION = {
    'text/plain': 'txt',
    'text/html': 'html',
    'text/csv': 'csv',
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
};

const extensionFromMimeType = value => {
    if (!value) return null;
    const mime = String(value).toLowerCase().split(';')[0].trim();
    return MIME_TO_EXTENSION[mime] || null;
};

/** Prefer an explicit extension, then the stored file URL. A display name like "0.25% tax" is not a file type. */
export const getFileExtensionFromFile = file => {
    if (!file) return null;
    const known = new Set([...PDFTRON_SUPPORTED_EXTENSIONS, ...NATIVE_TEXT_PREVIEW_EXTENSIONS]);
    const knownExtension = value => {
        const ext = getFileExtensionFromUrl(value);
        return ext && known.has(ext) ? ext : null;
    };
    const explicit = file.extension ? String(file.extension).replace(/^\./, '').toLowerCase() : '';
    if (known.has(explicit)) return explicit;
    const fromMime = extensionFromMimeType(file.extension) || extensionFromMimeType(file.document_type);
    if (fromMime && known.has(fromMime)) return fromMime;
    const docTypeExt = file.document_type
        ? String(file.document_type).replace(/^\./, '').toLowerCase()
        : '';
    if (known.has(docTypeExt)) return docTypeExt;
    return (
        knownExtension(file.original_filename) ||
        knownExtension(file.document) ||
        knownExtension(file.file) ||
        knownExtension(file.document_name) ||
        null
    );
};

/**
 * Extension passed to WebViewer load options.
 * Markdown is plain text; WebViewer Server supports txt, not md natively.
 */
export const getWebViewerLoadExtension = extension => {
    const ext = String(extension || '').toLowerCase();
    if (ext === 'md' || ext === 'markdown') return 'txt';
    return ext || 'pdf';
};

export const isDocxExtension = extension => {
    return String(extension || '').toLowerCase() === 'docx';
};

export const isDocxFile = file => isDocxExtension(getFileExtensionFromFile(file));

export const isNativeTextPreviewExtension = extension => {
    return NATIVE_TEXT_PREVIEW_EXTENSIONS.includes(String(extension || '').toLowerCase());
};

export const isNativeTextPreviewFile = file => isNativeTextPreviewExtension(getFileExtensionFromFile(file));

/** @returns {'html' | 'plain'} */
export const getNativeTextPreviewMode = extension => {
    const ext = String(extension || '').toLowerCase();
    return ext === 'html' || ext === 'htm' ? 'html' : 'plain';
};

export const canPreviewFile = file => isPDFTronSupportedFile(file) || isNativeTextPreviewFile(file);

/**
 * How WebViewer should open this file (all via WebViewer Server / PDF-style view).
 * - pdf: PDF via WebViewer Server
 * - office-pdf: .doc / .docx / .ppt / .pptx via server conversion
 * - server: spreadsheets, images, text, etc. via server conversion
 */
export const getWebViewerPreviewKind = extension => {
    const ext = String(extension || '').toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (OFFICE_SERVER_PDF_EXTENSIONS.includes(ext)) return 'office-pdf';
    return 'server';
};

/** Citation search/highlight only works on paginated PDF-style views. */
export const supportsPdfCitationHighlight = extension => {
    const kind = getWebViewerPreviewKind(extension);
    return kind === 'pdf' || kind === 'office-pdf' || kind === 'server';
};

/** WebViewer Server fetches the blob (not the user's browser). */
export const usesWebViewerServerForBlobFetch = () => true;

export const getWebViewerLoadDocumentOptions = ({ extension, filename }) => {
    const options = {
        extension: getWebViewerLoadExtension(extension),
    };
    if (filename) {
        options.filename = filename;
    }
    if (isSpreadsheetPreviewExtension(extension)) {
        options.officeOptions = getSpreadsheetOfficeOptions();
    }
    return options;
};

/**
 * WebViewer.Iframe constructor options.
 * All preview types use WebViewer Server so Azure blob access stays on the VNet /
 * allowlisted App Service IPs (pdftron-lens-server).
 * Spreadsheets omit initialDoc so ExcelMaxAllowedCellCount is applied on loadDocument.
 */
export const getWebViewerIframeOptions = ({ fileUrl, extension, filename }) => {
    const options = {
        path: '/lib/webviewer',
        licenseKey: process.env.REACT_APP_PDF_EXPRESS_KEY,
        webviewerServerURL: process.env.REACT_APP_PDF_WEBVIEWER_URL,
        ...getWebViewerLoadDocumentOptions({ extension, filename }),
    };
    if (!isSpreadsheetPreviewExtension(extension)) {
        options.initialDoc = fileUrl;
    }
    return options;
};

export const isPDFTronSupportedExtension = extension => {
    if (!extension) return false;
    return PDFTRON_SUPPORTED_EXTENSIONS.includes(String(extension).toLowerCase());
};

export const isPDFTronSupportedFilename = filenameOrUrl => {
    return isPDFTronSupportedExtension(getFileExtensionFromUrl(filenameOrUrl));
};

export const isPDFTronSupportedFile = file => {
    if (!file) return false;
    if (typeof file.is_pdftron_supported === 'boolean') {
        return file.is_pdftron_supported;
    }
    return isPDFTronSupportedExtension(getFileExtensionFromFile(file));
};

/** @param {{ original_filename?: string, file?: string, extension?: string, is_pdftron_supported?: boolean }} attachment */
export const isPDFTronSupportedAttachment = attachment => isPDFTronSupportedFile(attachment);

/** Shape attachment as a minimal file object for PDFTron loaders expecting `document`. */
export const attachmentToViewerFile = attachment => ({
    id: attachment?.id,
    document: attachment?.file,
    document_name: attachment?.original_filename,
    extension: attachment?.extension || getFileExtensionFromUrl(attachment?.original_filename || attachment?.file),
});
